import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import productListBrandDetailsHandler from "@MasterBluePrint/Store/Data/ProductList/Handlers/ProductListBrandDetails";
import themeColors from "@MasterBluePrint/Styles/variables";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showBrand = "showBrand",
    maximumBrandImageHeight = "maximumBrandImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showBrand]: boolean;
        [fields.maximumBrandImageHeight]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const brandsState = getBrandsDataView(state, {});

    return {
        state,
        brandsState,
        path,
        currentPageUrl,
    };
};
const mapDispatchToProps = {
    productListBrandDetailsHandler,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    link?: LinkPresentationProps;
    image?: LazyImageProps;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-bottom: 1.25rem;
            border-top: 1px solid ${themeColors.colorBorderBrands};
            border-bottom: 1px solid ${themeColors.colorBorderBrands};
            display: flex;
            width: 100%;
            min-height: 7rem;
        `,
    },

    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    link: {
        css: css`
            margin: 0.3125rem 0 0.3125rem 1.25rem;
        `,
    },
};

const styles = listStyles;

const ProductListBrandDetails = ({
    productListBrandDetailsHandler,
    state,
    brandsState,
    path,
    fields,
    currentPageUrl,
}: Props) => {
    const isBrandPage = currentPageUrl.toLowerCase().includes("brand");
    const [brand, setBrandDetails] = React.useState<BrandModel>();
    const getBrandDetails = (brandModel: BrandModel) => {
        if (brandModel && brandModel.htmlContent) {
            // if there is a body tag, keep only what's between the body tag, then change those characters so the whole string is wrapped in a div tag instead
            const data = JSON.parse(JSON.stringify(brandModel));
            let updatedHtmlContent = data.htmlContent;
            const bodyTag = data.htmlContent.indexOf("<body");
            if (bodyTag > -1) {
                updatedHtmlContent = `<div${data.htmlContent.substring(
                    bodyTag + 5,
                    data.htmlContent.indexOf("</body>"),
                )}</div>`;
            }
            data.htmlContent = updatedHtmlContent;
            setBrandDetails(data);
        } else {
            setBrandDetails(brandModel);
        }
    };
    const loadBrandDetails = () => {
        productListBrandDetailsHandler({ path, onSuccess: getBrandDetails });
    };
    useEffect(() => {
        if (!brandsState.value && !brandsState.isLoading) {
            loadBrandDetails();
        }
    }, []);
    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${fields.maximumBrandImageHeight}px`,
        },
    };
    return (
        <>
            {isBrandPage && brand && !brand?.htmlContent && (
                <StyledWrapper {...styles.wrapper} className="brandLogoWrapper">
                    {fields.showBrand && (
                        <Typography {...styles.centeringWrapper} id="brandDetails">
                            {brand?.name}
                        </Typography>
                    )}
                    {fields.showImage && (
                        <Clickable
                            href={`${brand?.detailPagePath}/category/all`}
                            {...styles.link}
                            className="brandImgLink"
                        >
                            <LazyImage
                                imgProps={imgProps}
                                {...styles.image}
                                src={brand?.logoSmallImagePath}
                                altText={`${translate("Go to brand page")} ${brand?.logoAltText || brand?.name}`}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
            )}
            {isBrandPage &&
                brand &&
                brand?.htmlContent !== null &&
                brand?.htmlContent !== undefined &&
                brand?.htmlContent !== "" && (
                    <StyledWrapper {...styles.container} data-test-selector="brandContent">
                        {parse(brand.htmlContent, parserOptions)}
                    </StyledWrapper>
                )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListBrandDetails),
    definition: {
        group: "Product List",
        displayName: "Brand Details",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Brand Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "General",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
